import { useId } from 'react';
import type { ParseKeys } from 'i18next';
import {
  maskCpf,
  type MiceGender,
  type MiceProfileChoice,
  type MiceProfileField,
  type MiceProfileFieldView,
} from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { Button, Input, Label } from '@web/ui/core/atoms';
import { ToneChip, type ToneChipTone } from '@web/ui/core/molecules';
import {
  birthdateInvalid,
  cpfInvalid,
  maskBirthdate,
  type ProfileDraft,
  type ProfileRowMode,
} from '../travelers/profile_draft';
import { formatFullDate } from './need_format';

const FIELD_LABEL = {
  name: 'mice.profile.field.name',
  birthdate: 'mice.profile.field.birthdate',
  cpf: 'mice.profile.field.cpf',
  gender: 'mice.profile.field.gender',
} as const;

const GENDER_LABEL = {
  F: 'mice.profile.gender.F',
  M: 'mice.profile.gender.M',
} as const satisfies Record<MiceGender, ParseKeys>;

/** `F`/`M` por extenso; outro valor (não deveria vir) aparece como está. */
export const genderLabel = (value: string): string =>
  value === 'F' || value === 'M' ? t(GENDER_LABEL[value]) : value;

const STATE_CHIP = {
  voll: { tone: 'gray', key: 'mice.profile.state.voll' },
  equal: { tone: 'green', key: 'mice.profile.state.equal' },
  to_complete: { tone: 'amber', key: 'mice.profile.state.to_complete' },
  divergent: { tone: 'amber', key: 'mice.profile.state.divergent' },
  missing: { tone: 'red', key: 'mice.profile.state.missing' },
} as const satisfies Record<
  MiceProfileFieldView['state'] | 'missing',
  { tone: ToneChipTone; key: ParseKeys }
>;

/** Os botões de cada estado: completar grava ou não; divergir usa a planilha ou mantém a VOLL. */
const CHOICE_LABEL = {
  to_complete: { sheet: 'mice.profile.choice.write', voll: 'mice.profile.choice.skip' },
  divergent: { sheet: 'mice.profile.choice.useSheet', voll: 'mice.profile.choice.keepVoll' },
} as const;

/** Sexo a completar não grava na VOLL (#2639): vale só na emissão do MICE. */
const GENDER_COMPLETE_LABEL = {
  sheet: 'mice.profile.choice.useForBooking',
  voll: 'mice.profile.choice.dontUse',
} as const;

/** Valor como a tela mostra: nascimento em DD/MM/AAAA, sexo por extenso; o CPF já vem mascarado
 *  do servidor. */
const displayOf = (field: MiceProfileField, value: string | null): string => {
  if (value === null) return '—';
  if (field === 'gender') return genderLabel(value);
  return field === 'birthdate' ? formatFullDate(value) : value;
};

interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Erro de formato: anunciado e ligado ao campo. */
  error?: string | null;
  placeholder?: string;
  numeric?: boolean;
}

function TextField(props: Readonly<TextFieldProps>): React.JSX.Element {
  const id = useId();
  const errorId = `${id}-error`;
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <Label htmlFor={id}>{props.label}</Label>
      <Input
        id={id}
        size="sm"
        value={props.value}
        autoComplete="off"
        placeholder={props.placeholder}
        inputMode={props.numeric ? 'numeric' : undefined}
        aria-invalid={props.error ? true : undefined}
        aria-describedby={props.error ? errorId : undefined}
        onChange={(e) => {
          props.onChange(e.target.value);
        }}
      />
      <span id={errorId} aria-live="polite" className="text-micro text-destructive">
        {props.error ?? ''}
      </span>
    </div>
  );
}

type OnDraft = (next: Partial<ProfileDraft>) => void;

/** Sexo: um dos dois, escolhido; nada vem marcado. */
function GenderInputs({ draft, onDraft }: Readonly<{ draft: ProfileDraft; onDraft: OnDraft }>) {
  return (
    <div className="flex flex-wrap gap-1">
      {(['F', 'M'] as const).map((gender) => (
        <Button
          key={gender}
          size="sm"
          variant={draft.gender === gender ? 'primary' : 'outline'}
          aria-pressed={draft.gender === gender}
          onClick={() => {
            onDraft({ gender: draft.gender === gender ? null : gender });
          }}
        >
          {t(GENDER_LABEL[gender])}
        </Button>
      ))}
    </div>
  );
}

/** Os campos do formulário de um dado: Nome e Sobrenome sempre separados, como no documento. */
function ProfileInputs({
  field,
  draft,
  onDraft,
}: Readonly<{ field: MiceProfileField; draft: ProfileDraft; onDraft: OnDraft }>) {
  if (field === 'name') {
    return (
      <div className="flex flex-wrap gap-2">
        <TextField
          label={t('mice.profile.input.firstName')}
          value={draft.firstName}
          onChange={(firstName) => {
            onDraft({ firstName });
          }}
        />
        <TextField
          label={t('mice.profile.input.lastName')}
          value={draft.lastName}
          onChange={(lastName) => {
            onDraft({ lastName });
          }}
        />
      </div>
    );
  }
  if (field === 'gender') return <GenderInputs draft={draft} onDraft={onDraft} />;
  if (field === 'birthdate') {
    return (
      <TextField
        label={t('mice.profile.field.birthdate')}
        value={draft.birthdate}
        placeholder={t('mice.profile.input.birthdatePlaceholder')}
        numeric
        error={birthdateInvalid(draft.birthdate) ? t('mice.profile.input.birthdateInvalid') : null}
        onChange={(value) => {
          onDraft({ birthdate: maskBirthdate(value) });
        }}
      />
    );
  }
  return (
    <TextField
      label={t('mice.profile.field.cpf')}
      value={draft.cpf}
      placeholder={t('mice.profile.input.cpfPlaceholder')}
      numeric
      error={cpfInvalid(draft.cpf) ? t('mice.profile.input.cpfInvalid') : null}
      onChange={(value) => {
        onDraft({ cpf: maskCpf(value) });
      }}
    />
  );
}

function ChoiceButtons({
  labels,
  value,
  onChoose,
}: Readonly<{
  labels: Record<MiceProfileChoice, ParseKeys>;
  value: MiceProfileChoice | undefined;
  onChoose: (choice: MiceProfileChoice | undefined) => void;
}>) {
  return (
    <div className="flex flex-wrap gap-1">
      {(['sheet', 'voll'] as const).map((choice) => (
        <Button
          key={choice}
          size="sm"
          variant={value === choice ? 'primary' : 'outline'}
          aria-pressed={value === choice}
          onClick={() => {
            onChoose(value === choice ? undefined : choice);
          }}
        >
          {t(labels[choice])}
        </Button>
      ))}
    </div>
  );
}

interface ProfileFieldRowProps {
  field: MiceProfileField;
  view: MiceProfileFieldView;
  mode: ProfileRowMode;
  /** Sem permissão de compra: só os valores. */
  canResolve: boolean;
  /** Mostrar o formulário do campo (falta o valor, ou "Usar o da planilha" no nome). */
  typing: boolean;
  draft: ProfileDraft;
  onDraft: OnDraft;
}

/** Uma linha por dado: o valor da planilha, o da VOLL, o estado e o que dá para fazer. */
export function ProfileFieldRow(props: Readonly<ProfileFieldRowProps>): React.JSX.Element {
  const { field, view, mode } = props;
  const labelId = useId();
  const chip = STATE_CHIP[mode === 'type' ? 'missing' : view.state];
  const decidable = view.state === 'to_complete' || view.state === 'divergent' ? view.state : null;
  return (
    <div
      role="group"
      aria-labelledby={labelId}
      className="flex flex-col gap-2 rounded-md border border-border-soft p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <span id={labelId} className="text-meta font-medium text-foreground">
          {t(FIELD_LABEL[field])}
        </span>
        <ToneChip tone={chip.tone}>{t(chip.key)}</ToneChip>
      </div>
      <dl className="grid grid-cols-2 gap-2 text-meta">
        <div className="flex min-w-0 flex-col">
          <dt className="text-micro text-muted-foreground">{t('mice.profile.sheet')}</dt>
          <dd className="break-words text-foreground">{displayOf(field, view.sheet)}</dd>
        </div>
        <div className="flex min-w-0 flex-col">
          <dt className="text-micro text-muted-foreground">{t('mice.profile.voll')}</dt>
          <dd className="break-words text-foreground">{displayOf(field, view.voll)}</dd>
        </div>
      </dl>
      {/* As máscaras dos dois lados podem ser idênticas: só o texto diz que os dígitos diferem. */}
      {field === 'cpf' && view.state === 'divergent' ? (
        <p className="text-micro text-muted-foreground">{t('mice.profile.cpfDiffers')}</p>
      ) : null}
      {props.canResolve && mode === 'decide' && decidable ? (
        <ChoiceButtons
          labels={
            field === 'gender' && decidable === 'to_complete'
              ? GENDER_COMPLETE_LABEL
              : CHOICE_LABEL[decidable]
          }
          value={props.draft.choices[field]}
          onChoose={(choice) => {
            props.onDraft({ choices: { ...props.draft.choices, [field]: choice } });
          }}
        />
      ) : null}
      {props.canResolve && props.typing ? (
        <ProfileInputs field={field} draft={props.draft} onDraft={props.onDraft} />
      ) : null}
    </div>
  );
}
